// ============================================================================
// FinanceYearChart (E2, F-522) — evolution janv..dec : reel puis prevu (budget)
// ============================================================================
// Lot GEST-E2-EVOLUTION-LISIBILITE (2026-08-04) : echelles separees (revenus/
// depenses en lignes, benefice net en barres), axe Y + grilles, direct labeling,
// etiquettes selectives, frontiere reel/prevu materialisee, tooltip crosshair.
// SVG maison — AUCUNE dependance de charting.
//
// COULEURS (BL-12) : revenus `--chart-cat-5`, depenses marque runtime
// `rgb(var(--brand-500-rgb))`, net `--chart-cat-3` ; barres negatives =
// echelle nommee varianceToneClass (pas de hex).
// Adherence #28 : tokens DS, echelle nommee text-chip / text-meta / text-field.

import { useCallback, useEffect, useId, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { TFunction } from 'i18next';
import type { FinanceYearPoint } from '../../hooks/useFinance';
import {
  formatCad,
  formatCadCompact,
  formatMonthShort,
  formatSignedCad,
  varianceToneClass,
} from '../../pages/finance/format';

type LineKey = 'revenue' | 'expense';
type Variant = 'actual' | 'budget' | 'priorYear';

const LINE_SERIES: ReadonlyArray<{ key: LineKey; labelKey: string; color: string }> = [
  { key: 'revenue', labelKey: 'gest:finance.series.revenue', color: 'var(--chart-cat-5)' },
  { key: 'expense', labelKey: 'gest:finance.series.expense', color: 'rgb(var(--brand-500-rgb))' },
];

/** Pastilles et legende etroite — inclut le benefice net. */
const LEGEND_SERIES: ReadonlyArray<{ key: LineKey | 'net'; labelKey: string; color: string }> = [
  ...LINE_SERIES,
  { key: 'net', labelKey: 'gest:finance.series.net', color: 'var(--chart-cat-3)' },
];

const NET_COLOR = 'var(--chart-cat-3)';
// Identifiants de serie (cles de donnees, pas du texte) lus dans le rendu.
const SERIES_REVENUE: LineKey = 'revenue';
const SERIES_EXPENSE: LineKey = 'expense';
const SERIES_NET = 'net' as const;
const DIRECT_LABEL_X_INSET = 4;
const DIRECT_CLIP_INSET = 4;
const DIRECT_LABEL_LINE_GAP = 12;
const DIRECT_LABEL_BLOCK_H = 26;
const CARD = 'rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800';
const NARROW_W = 560;
const PAD_L = 52;
const PAD_R = 76;
const LABEL_ZONE = 22;
const SUB_GAP = 4;

export interface FinanceYearChartProps {
  points: FinanceYearPoint[];
  locale: string;
  t: TFunction;
}

/** Valeur effectivement tracee pour un mois (reel ou budget prevu). */
function tracedValue(p: FinanceYearPoint, key: LineKey | 'net'): number {
  return p.isForecast ? p[key].budget : p[key].actual;
}

/** Graduations Y lisibles (3 a 5 ticks). */
function niceTicks(min: number, max: number, target = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0];
  if (min === max) return [min];
  const span = max - min;
  const rawStep = span / target;
  const mag = 10 ** Math.floor(Math.log10(Math.abs(rawStep) || 1));
  const step = Math.ceil(rawStep / mag) * mag || mag;
  const ticks: number[] = [];
  const start = Math.floor(min / step) * step;
  for (let v = start; v <= max + step * 0.001; v += step) {
    if (v >= min - step * 0.001 && v <= max + step * 0.001) ticks.push(v);
  }
  if (min < 0 && max > 0 && !ticks.some((t) => Math.abs(t) < step * 0.001)) {
    ticks.push(0);
  }
  return [...new Set(ticks.map((v) => Math.round(v * 1000) / 1000))].sort((a, b) => a - b);
}

interface ValueLabel {
  id: string;
  x: number;
  y: number;
  text: string;
  color: string;
  priority: number;
}

function resolveCollisions(labels: ValueLabel[], minGap = 14): ValueLabel[] {
  const sorted = [...labels].sort((a, b) => a.priority - b.priority || a.x - b.x);
  const placed: ValueLabel[] = [];
  for (const lbl of sorted) {
    let y = lbl.y;
    for (const other of placed) {
      if (Math.abs(other.x - lbl.x) < 36 && Math.abs(other.y - y) < minGap) {
        y = other.y - minGap;
      }
    }
    placed.push({ ...lbl, y });
  }
  return placed;
}

interface DirectLabel {
  id: string;
  x: number;
  y: number;
  yMin: number;
  yMax: number;
  name: string;
  value: string;
  fill: string;
  className: string;
  priority: number;
}

/** Collision verticale des direct labels (colonne PAD_R), avec bornes par zone. */
function resolveDirectLabelCollisions(labels: DirectLabel[], minGap = DIRECT_LABEL_BLOCK_H): DirectLabel[] {
  const sorted = [...labels].sort((a, b) => a.priority - b.priority || a.y - b.y);
  const placed: DirectLabel[] = [];
  for (const lbl of sorted) {
    let y = lbl.y;
    for (const other of placed) {
      if (Math.abs(other.y - y) < minGap) {
        y = other.y - minGap;
      }
    }
    y = Math.max(lbl.yMin, Math.min(lbl.yMax, y));
    placed.push({ ...lbl, y });
  }
  return placed;
}

export function FinanceYearChart({ points, locale, t }: FinanceYearChartProps) {
  const [showBudget, setShowBudget] = useState(false);
  const [showPriorYear, setShowPriorYear] = useState(false);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const directClipRawId = useId();
  const directClipId = `direct-label-clip-${directClipRawId.replace(/:/g, '')}`;
  const [width, setWidth] = useState(720);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth || 720);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const year = points[0]?.period.split('-')[0] ?? '';

  const lastRealIdx = useMemo(
    () => points.reduce((last, p, i) => (p.isForecast ? last : i), -1),
    [points],
  );
  const hasForecast = useMemo(() => points.some((p) => p.isForecast), [points]);
  const lastIdx = points.length - 1;
  const narrow = width < NARROW_W;

  const layout = useMemo(() => {
    const subChartH = Math.round(Math.min(88, Math.max(56, width / 9)));
    const mainChartH = Math.round(Math.min(260, Math.max(130, width / 4.2)));
    const height = mainChartH + SUB_GAP + subChartH + LABEL_ZONE;
    const innerW = width - PAD_L - PAD_R;
    return { subChartH, mainChartH, height, innerW };
  }, [width]);

  const xFn = useCallback(
    (i: number) =>
      points.length === 1 ? PAD_L + layout.innerW / 2 : PAD_L + (i / (points.length - 1)) * layout.innerW,
    [points.length, layout.innerW],
  );

  const lineScale = useMemo(() => {
    const values: number[] = [];
    for (const p of points) {
      for (const s of LINE_SERIES) {
        values.push(tracedValue(p, s.key));
        if (showBudget) values.push(p[s.key].budget);
        if (showPriorYear) values.push(p[s.key].priorYear);
      }
    }
    const top = Math.max(0, ...values, 1);
    const bottom = Math.min(0, ...values);
    const span = top - bottom || 1;
    const y = (val: number) => (1 - (val - bottom) / span) * (layout.mainChartH - 4) + 2;
    return { top, bottom, span, y, ticks: niceTicks(bottom, top) };
  }, [points, showBudget, showPriorYear, layout.mainChartH]);

  const netScale = useMemo(() => {
    const values: number[] = [];
    for (const p of points) {
      values.push(tracedValue(p, 'net'));
      if (showBudget) values.push(p.net.budget);
      if (showPriorYear) values.push(p.net.priorYear);
    }
    const top = Math.max(0, ...values, 1);
    const bottom = Math.min(0, ...values);
    const span = top - bottom || 1;
    const subTop = layout.mainChartH + SUB_GAP;
    const y = (val: number) => subTop + (1 - (val - bottom) / span) * (layout.subChartH - 4) + 2;
    return { top, bottom, span, y, zeroY: y(0), subTop, ticks: niceTicks(bottom, top, 2) };
  }, [points, showBudget, showPriorYear, layout.mainChartH, layout.subChartH]);

  const polyline = useCallback(
    (key: LineKey, variant: Variant, from: number, to: number) => {
      const pts: string[] = [];
      for (let i = from; i <= to; i++) {
        pts.push(`${xFn(i).toFixed(1)},${lineScale.y(points[i][key][variant]).toFixed(1)}`);
      }
      return pts.join(' ');
    },
    [xFn, lineScale, points],
  );

  const forecastPolyline = useCallback(
    (key: LineKey) => {
      if (!hasForecast || lastRealIdx < 0) return '';
      const pts = [`${xFn(lastRealIdx).toFixed(1)},${lineScale.y(points[lastRealIdx][key].actual).toFixed(1)}`];
      for (let i = lastRealIdx + 1; i < points.length; i++) {
        pts.push(`${xFn(i).toFixed(1)},${lineScale.y(points[i][key].budget).toFixed(1)}`);
      }
      return pts.join(' ');
    },
    [hasForecast, lastRealIdx, xFn, lineScale, points],
  );

  const endPointY = useCallback(
    (key: LineKey) => {
      const p = points[lastIdx];
      if (!p) return lineScale.y(0);
      return lineScale.y(tracedValue(p, key));
    },
    [points, lastIdx, lineScale],
  );

  const selectiveLabelIndices = useCallback(
    (key: LineKey) => {
      const indices = new Set<number>();
      if (lastRealIdx >= 0) indices.add(lastRealIdx);
      if (hasForecast && lastIdx >= 0) indices.add(lastIdx);
      if (!narrow) {
        let maxIdx = 0;
        let maxVal = -Infinity;
        points.forEach((p, i) => {
          const v = tracedValue(p, key);
          if (v > maxVal) {
            maxVal = v;
            maxIdx = i;
          }
        });
        indices.add(maxIdx);
      }
      return [...indices];
    },
    [points, lastRealIdx, hasForecast, lastIdx, narrow],
  );

  const valueLabels = useMemo(() => {
    const labels: ValueLabel[] = [];
    for (const s of LINE_SERIES) {
      for (const i of selectiveLabelIndices(s.key)) {
        const p = points[i];
        if (!p) continue;
        const val = tracedValue(p, s.key);
        labels.push({
          id: `${s.key}-${i}`,
          x: xFn(i),
          y: lineScale.y(val) - 8,
          text: formatCadCompact(val, locale),
          color: s.color,
          priority: i === lastRealIdx ? 0 : i === lastIdx ? 1 : 2,
        });
      }
    }
    return resolveCollisions(labels);
  }, [points, selectiveLabelIndices, xFn, lineScale, locale, lastRealIdx, lastIdx]);

  const nearestMonth = useCallback(
    (svgX: number) => {
      let nearest = 0;
      let minDist = Infinity;
      for (let i = 0; i < points.length; i++) {
        const dist = Math.abs(xFn(i) - svgX);
        if (dist < minDist) {
          minDist = dist;
          nearest = i;
        }
      }
      return nearest;
    },
    [points.length, xFn],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      setHoverIdx(nearestMonth(e.clientX - rect.left));
    },
    [nearestMonth],
  );

  const handleMouseLeave = useCallback(() => setHoverIdx(null), []);

  const directLabelX = width - PAD_R + DIRECT_LABEL_X_INSET;

  const directLabels = useMemo((): DirectLabel[] => {
    if (narrow || points.length === 0) return [];

    const lastNetVal = tracedValue(points[lastIdx], 'net');
    const lastNetNeg = lastNetVal < 0;
    const mainYMax = layout.mainChartH - DIRECT_LABEL_BLOCK_H;
    const subYMin = netScale.subTop + 4;
    const subYMax = layout.mainChartH + SUB_GAP + layout.subChartH - DIRECT_LABEL_BLOCK_H - 4;

    const items: DirectLabel[] = [
      ...LINE_SERIES.map((s, i) => ({
        id: s.key,
        x: directLabelX,
        y: endPointY(s.key) + 4,
        yMin: 12,
        yMax: mainYMax,
        name: t(s.labelKey),
        value: formatCadCompact(tracedValue(points[lastIdx], s.key), locale),
        fill: s.color,
        className: 'text-chip font-medium',
        priority: i,
      })),
      {
        id: 'net',
        x: directLabelX,
        y: netScale.y(lastNetVal) + 4,
        yMin: subYMin,
        yMax: subYMax,
        name: t('gest:finance.series.net'),
        value: formatCadCompact(lastNetVal, locale),
        fill: lastNetNeg ? 'currentColor' : NET_COLOR,
        className: `text-chip font-medium${lastNetNeg ? ` ${varianceToneClass(lastNetVal)}` : ''}`,
        priority: LINE_SERIES.length,
      },
    ];

    return resolveDirectLabelCollisions(items);
  }, [
    narrow,
    points,
    lastIdx,
    directLabelX,
    layout.mainChartH,
    layout.subChartH,
    netScale,
    endPointY,
    locale,
    t,
  ]);

  if (points.length === 0) {
    return (
      <section className={CARD}>
        <h2 className="text-head font-semibold text-slate-800 dark:text-slate-100">
          {t('gest:finance.yearChart.emptyTitle')}
        </h2>
        <p className="mt-1 text-field text-slate-500 dark:text-slate-400">
          {t('gest:finance.yearChart.empty')}
        </p>
      </section>
    );
  }

  const ariaLabel = t('gest:finance.yearChart.aria', {
    year,
    detail: points
      .map((p) => {
        const v = (amounts: { actual: number; budget: number }) =>
          formatCad(p.isForecast ? amounts.budget : amounts.actual, locale);
        const month = formatMonthShort(p.period, locale);
        const suffix = p.isForecast ? ` (${t('gest:finance.yearChart.forecast')})` : '';
        return `${month}${suffix} ${t('gest:finance.series.revenue')} ${v(p.revenue)}, ${t('gest:finance.series.expense')} ${v(p.expense)}, ${t('gest:finance.series.net')} ${v(p.net)}`;
      })
      .join(' ; '),
  });

  const dashLegend = [
    showBudget ? t('gest:finance.series.budget') : '',
    showPriorYear ? t('gest:finance.series.priorYear') : '',
  ]
    .filter(Boolean)
    .join(' / ');

  const barW =
    points.length > 1 ? Math.min(22, (layout.innerW / points.length) * 0.55) : 22;

  const hoverPoint = hoverIdx !== null ? points[hoverIdx] : null;
  const tooltipX = hoverIdx !== null ? xFn(hoverIdx) : 0;
  const tooltipFlip = tooltipX > width - PAD_R - 120;

  return (
    <section className={CARD}>
      <h2 className="text-head font-semibold text-slate-800 dark:text-slate-100">
        {t('gest:finance.yearChart.title', { year })}
      </h2>
      <p className="mt-1 text-field text-slate-500 dark:text-slate-400">
        {t('gest:finance.yearChart.description')}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <FilterChip
          label={t('gest:finance.series.budget')}
          active={showBudget}
          onClick={() => setShowBudget((v) => !v)}
        />
        <FilterChip
          label={t('gest:finance.series.priorYear')}
          active={showPriorYear}
          onClick={() => setShowPriorYear((v) => !v)}
        />
      </div>

      <div ref={containerRef} className="mt-3 w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${layout.height}`}
          width={width}
          height={layout.height}
          role="img"
          aria-label={ariaLabel}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {!narrow ? (
            <defs>
              <clipPath id={directClipId}>
                <rect
                  x={width - PAD_R + DIRECT_CLIP_INSET}
                  y={0}
                  width={PAD_R - DIRECT_CLIP_INSET * 2}
                  height={layout.mainChartH + SUB_GAP + layout.subChartH}
                />
              </clipPath>
            </defs>
          ) : null}

          {/* Zone prevue — fond leger sur les deux graphiques */}
          {hasForecast && lastRealIdx >= 0 && lastRealIdx < lastIdx ? (
            <rect
              pointerEvents="none"
              x={xFn(lastRealIdx)}
              y={0}
              width={width - PAD_R - xFn(lastRealIdx)}
              height={layout.mainChartH + SUB_GAP + layout.subChartH}
              className="fill-slate-100/80 dark:fill-slate-700/40"
            />
          ) : null}

          {/* Grille + etiquettes axe Y — graphique principal (revenus / depenses) */}
          {lineScale.ticks.map((tick) => (
            <g key={`main-grid-${tick}`}>
              <line
                x1={PAD_L}
                x2={width - PAD_R}
                y1={lineScale.y(tick)}
                y2={lineScale.y(tick)}
                strokeWidth={1}
                className="stroke-slate-200/80 dark:stroke-slate-600/50"
              />
              <text
                x={PAD_L - 6}
                y={lineScale.y(tick) + 4}
                textAnchor="end"
                className="fill-slate-400 text-chip dark:fill-slate-500"
              >
                {formatCadCompact(tick, locale)}
              </text>
            </g>
          ))}

          {/* Grille + etiquettes axe Y — sous-graphique net */}
          {netScale.ticks.map((tick) => (
            <g key={`net-grid-${tick}`}>
              <line
                x1={PAD_L}
                x2={width - PAD_R}
                y1={netScale.y(tick)}
                y2={netScale.y(tick)}
                strokeWidth={1}
                className="stroke-slate-200/80 dark:stroke-slate-600/50"
              />
              <text
                x={PAD_L - 6}
                y={netScale.y(tick) + 4}
                textAnchor="end"
                className="fill-slate-400 text-chip dark:fill-slate-500"
              >
                {formatCadCompact(tick, locale)}
              </text>
            </g>
          ))}

          {/* Ligne zero — sous-graphique net uniquement */}
          <line
            x1={PAD_L}
            x2={width - PAD_R}
            y1={netScale.zeroY}
            y2={netScale.zeroY}
            strokeWidth={1}
            className="stroke-slate-300 dark:stroke-slate-600"
          />

          {/* Frontiere reel / prevu — label en bas du graphique principal (evite collision tooltip) */}
          {hasForecast && lastRealIdx >= 0 ? (
            <g pointerEvents="none">
              <line
                x1={xFn(lastRealIdx)}
                x2={xFn(lastRealIdx)}
                y1={0}
                y2={layout.mainChartH + SUB_GAP + layout.subChartH}
                strokeWidth={1}
                strokeDasharray="4 3"
                className="stroke-slate-400 dark:stroke-slate-500"
              />
              <text
                x={xFn(lastRealIdx) + 4}
                y={layout.mainChartH - 6}
                className="fill-slate-500 text-chip dark:fill-slate-400"
              >
                {t('gest:finance.yearChart.realForecast')}
              </text>
            </g>
          ) : null}

          {/* Courbes revenus / depenses */}
          {LINE_SERIES.map((s) => (
            <g key={s.key}>
              {showPriorYear ? (
                <polyline
                  points={polyline(s.key, 'priorYear', 0, lastIdx)}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={1.5}
                  strokeDasharray="2 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.55}
                />
              ) : null}
              {showBudget ? (
                <polyline
                  points={polyline(s.key, 'budget', 0, lastIdx)}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.7}
                />
              ) : null}
              {hasForecast ? (
                <polyline
                  points={forecastPolyline(s.key)}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.7}
                />
              ) : null}
              {lastRealIdx >= 0 ? (
                <polyline
                  points={polyline(s.key, 'actual', 0, lastRealIdx)}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : null}
              {points.map((p, i) =>
                i <= lastRealIdx ? (
                  <circle key={`${s.key}-${p.period}`} cx={xFn(i)} cy={lineScale.y(p[s.key].actual)} r={2.5} fill={s.color} />
                ) : null,
              )}
            </g>
          ))}

          {/* Etiquettes de valeur selectives — lignes */}
          <g pointerEvents="none">
            {valueLabels.map((lbl) => (
              <text
                key={lbl.id}
                x={lbl.x}
                y={lbl.y}
                textAnchor="middle"
                fill={lbl.color}
                className="text-chip font-medium"
              >
                {lbl.text}
              </text>
            ))}
          </g>

          {/* Direct labeling — deux lignes (nom + valeur), collision verticale, clip filet */}
          {!narrow ? (
            <g pointerEvents="none" clipPath={`url(#${directClipId})`}>
              {directLabels.map((lbl) => (
                <text
                  key={`direct-${lbl.id}`}
                  x={lbl.x}
                  y={lbl.y}
                  textAnchor="start"
                  fill={lbl.fill}
                  className={lbl.className}
                >
                  <tspan x={lbl.x} dy={0}>
                    {lbl.name}
                  </tspan>
                  <tspan x={lbl.x} dy={DIRECT_LABEL_LINE_GAP}>
                    {lbl.value}
                  </tspan>
                </text>
              ))}
            </g>
          ) : null}

          {/* Barres benefice net */}
          {points.map((p, i) => {
            const val = tracedValue(p, SERIES_NET);
            const cx = xFn(i);
            const yTop = Math.min(netScale.y(val), netScale.zeroY);
            const barH = Math.max(1, Math.abs(netScale.y(val) - netScale.zeroY));
            const isNeg = val < 0;
            return (
              <rect
                key={`net-bar-${p.period}`}
                x={cx - barW / 2}
                y={yTop}
                width={barW}
                height={barH}
                rx={2}
                fill={isNeg ? 'currentColor' : NET_COLOR}
                className={isNeg ? varianceToneClass(val) : undefined}
                opacity={p.isForecast ? 0.45 : 1}
              />
            );
          })}

          {/* Etiquettes mois — axe X partage */}
          {points.map((p, i) => (
            <text
              key={p.period}
              x={xFn(i)}
              y={layout.height - 6}
              textAnchor="middle"
              className="fill-slate-400 text-chip dark:fill-slate-500"
            >
              {formatMonthShort(p.period, locale)}
            </text>
          ))}

          {/* Crosshair + tooltip */}
          {hoverPoint && hoverIdx !== null ? (
            <g pointerEvents="none">
              <line
                x1={tooltipX}
                x2={tooltipX}
                y1={0}
                y2={layout.mainChartH + SUB_GAP + layout.subChartH}
                strokeWidth={1}
                className="stroke-slate-400 dark:stroke-slate-500"
              />
              <rect
                x={tooltipFlip ? tooltipX - 148 : tooltipX + 8}
                y={8}
                width={140}
                height={88}
                rx={6}
                className="fill-white stroke-slate-200 dark:fill-slate-800 dark:stroke-slate-600"
                strokeWidth={1}
              />
              <text
                x={tooltipFlip ? tooltipX - 140 : tooltipX + 14}
                y={24}
                className="fill-slate-700 text-chip font-semibold dark:fill-slate-200"
              >
                {formatMonthShort(hoverPoint.period, locale)}
                {hoverPoint.isForecast ? ` (${t('gest:finance.yearChart.forecast')})` : ''}
              </text>
              <text
                x={tooltipFlip ? tooltipX - 140 : tooltipX + 14}
                y={40}
                className="fill-slate-600 text-chip dark:fill-slate-300"
              >
                {t('gest:finance.series.revenue')}: {formatCad(tracedValue(hoverPoint, SERIES_REVENUE), locale)}
              </text>
              <text
                x={tooltipFlip ? tooltipX - 140 : tooltipX + 14}
                y={54}
                className="fill-slate-600 text-chip dark:fill-slate-300"
              >
                {t('gest:finance.series.expense')}: {formatCad(tracedValue(hoverPoint, SERIES_EXPENSE), locale)}
              </text>
              <text
                x={tooltipFlip ? tooltipX - 140 : tooltipX + 14}
                y={68}
                className="fill-slate-600 text-chip dark:fill-slate-300"
              >
                {t('gest:finance.series.net')}: {formatCad(tracedValue(hoverPoint, SERIES_NET), locale)}
              </text>
              <text
                x={tooltipFlip ? tooltipX - 140 : tooltipX + 14}
                y={82}
                className={`text-chip ${varianceToneClass(hoverPoint.net.actual - hoverPoint.net.budget)}`}
              >
                {t('gest:finance.yearChart.vsBudget')}:{' '}
                {formatSignedCad(hoverPoint.net.actual - hoverPoint.net.budget, locale)}
              </text>
            </g>
          ) : null}
        </svg>
      </div>

      {/* Etroit : pastilles de serie ; large : direct labeling dans le SVG */}
      <div className="mt-2 flex flex-wrap items-center gap-4 text-meta text-slate-500 dark:text-slate-400">
        {narrow
          ? LEGEND_SERIES.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                {t(s.labelKey)}
              </span>
            ))
          : null}
        {hasForecast ? (
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-0 w-4 border-t border-dashed border-slate-400 dark:border-slate-500"
            />
            {t('gest:finance.yearChart.forecast')}
          </span>
        ) : null}
        {dashLegend ? (
          <span className="text-slate-400 dark:text-slate-500">
            {t('gest:finance.yearChart.dashLegend', { kinds: dashLegend })}
          </span>
        ) : null}
      </div>
    </section>
  );
}

/** Chip de bascule (aria-pressed) — standard « chip filtre actif » UI_STANDARDS §4. */
function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`touch-manipulation rounded-full px-3 py-1.5 text-field font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500/40 ${
        active
          ? 'bg-slate-800 font-semibold text-white dark:bg-slate-600'
          : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );
}
